import CodesModal from "molecules/modals/codes-modal";
import React from "react";
import { useLinkPreviewContext } from "utilities/contexts/link-preview/use-link-preview-context";
import usePublication from "utilities/hooks/domain/publications/use-publication";
import useLoading from "utilities/hooks/use-loading";
import useSectionIncludeSubSections from "utilities/hooks/domain/publications/chapters/sections/use-section-include-sub-sections";
import useOfflineSections from "utilities/hooks/domain/publications/chapters/sections/use-offline-sections";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import useIsNestedRoute from "utilities/hooks/routing/use-is-nested-route";
import useLatestSection from "utilities/hooks/domain/publications/chapters/sections/use-latest-section";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface LinkPreviewModalProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const LinkPreviewModal: React.FunctionComponent<LinkPreviewModalProps> = () => {
    const { isModalOpen, onGoToBookView, onModalClose, route, sectionId } =
        useLinkPreviewContext();

    const { isOnline } = useNetworkInformation();
    const { routeIsBookView } = useIsNestedRoute();

    const { publication: currentPublication } = useCurrentPublication();

    const { loading: loadingOnlineSection, resultObject: onlineSection } =
        useSectionIncludeSubSections({
            enabled: isOnline && !routeIsBookView,
            sectionId,
        });
    const { loading: loadingOfflineSection, resultObject: offlineSection } =
        useOfflineSections({
            enabled: !isOnline && !routeIsBookView,
            sectionId,
        });
    const { loading: loadingBookViewSection, resultObject: bookViewSection } =
        useLatestSection({
            enabled: routeIsBookView,
            sectionId: sectionId,
        });

    const {
        loading: loadingOnlinePublication,
        resultObject: onlinePublication,
    } = usePublication({ publicationId: onlineSection?.publicationId });

    const loadingPublication =
        isOnline && !routeIsBookView && loadingOnlinePublication;

    const loadingSection = routeIsBookView
        ? loadingBookViewSection
        : isOnline
          ? loadingOnlineSection
          : loadingOfflineSection;

    const publication = currentPublication ?? onlinePublication;

    const section = routeIsBookView
        ? bookViewSection
        : isOnline
          ? onlineSection
          : offlineSection;

    const loading = useLoading(loadingPublication, loadingSection);

    return (
        <CodesModal
            closeDialog={onModalClose}
            isLoading={loading}
            isVisible={isModalOpen}
            onClickOpenInBookView={onGoToBookView}
            route={route}
            content={section}
            subContent={section.subSections}
            publicationDisplayTitle={publication.getDisplayTitle()}
            publicationType={publication.type}
            enableReferencePanel={true}
            publication={publication}
            code={publication.code}
            edition={publication.edition}
        />
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Export
// -----------------------------------------------------------------------------------------

export default LinkPreviewModal;

// #endregion Export
